import { CAPACITY_STATUS_VALUES, deriveStatus } from '../utils/conversationLimit/capacity';

/**
 * Conversation limit ("modo cola") — pure reducer and action creators.
 *
 * `seatIds` is the set R7 filters realtime events with, and the source the
 * indicator's count is derived from on every seat event. `ownUnseatIds` are
 * the seats THIS tab is giving up (end chat, release), always a subset of
 * `seatIds`, so the "salió de tu cupo" notice is kept for what somebody else
 * did. Every action except
 * `VIEW_RECEIVED` — seats, capacity, queue AND the admin panels (load, config)
 * — is ignored while `enabled` is false, so with the feature off the slice
 * never leaves its initial state, even when a panel response arrives late.
 */

export const CONVERSATION_LIMIT_INITIAL_STATE = Object.freeze({
  enabled: false,
  capacity: Object.freeze({ limit: null, activeCount: 0, availableSlots: 0, status: 'available' }),
  seatIds: Object.freeze([]),
  ownUnseatIds: Object.freeze([]),
  queueWaiting: 0,
  load: Object.freeze({ fetching: false, data: null, error: null }),
  config: Object.freeze({ fetching: false, saving: false, data: null, error: null }),
  // "Carga por agente" kept live by Pusher: `at` = newest load applied per agent
  // (forgotten when the panel opens, like the app); `sinceLoad` /
  // `waitingSinceLoad` (+ when it arrived) = what came since the last /load
  // request, re-applied over its response (the snapshot may predate them).
  live: Object.freeze({
    at: Object.freeze({}), sinceLoad: Object.freeze({}), waitingSinceLoad: null, waitingSinceLoadAt: null,
  }),
});

const VIEW_RECEIVED = 'CONVERSATION_LIMIT_VIEW_RECEIVED';
const SEAT_ADDED = 'CONVERSATION_LIMIT_SEAT_ADDED';
const SEAT_REMOVED = 'CONVERSATION_LIMIT_SEAT_REMOVED';
const OWN_UNSEAT_STARTED = 'CONVERSATION_LIMIT_OWN_UNSEAT_STARTED';
const OWN_UNSEAT_SETTLED = 'CONVERSATION_LIMIT_OWN_UNSEAT_SETTLED';
const CAPACITY_RECEIVED = 'CONVERSATION_LIMIT_CAPACITY_RECEIVED';
const QUEUE_CHANGED = 'CONVERSATION_LIMIT_QUEUE_CHANGED';
const AGENT_LOAD_RECEIVED = 'CONVERSATION_LIMIT_AGENT_LOAD_RECEIVED';
const LOAD_REQUEST = 'CONVERSATION_LIMIT_LOAD_REQUEST';
const LOAD_SUCCESS = 'CONVERSATION_LIMIT_LOAD_SUCCESS';
const LOAD_FAILURE = 'CONVERSATION_LIMIT_LOAD_FAILURE';
const CONFIG_REQUEST = 'CONVERSATION_LIMIT_CONFIG_REQUEST';
const CONFIG_SAVE_REQUEST = 'CONVERSATION_LIMIT_CONFIG_SAVE_REQUEST';
const CONFIG_SUCCESS = 'CONVERSATION_LIMIT_CONFIG_SUCCESS';
const CONFIG_FAILURE = 'CONVERSATION_LIMIT_CONFIG_FAILURE';
const CLEAR_DATA = 'CLEAR_DATA'; // shared logout action (see conversationsDuck)

export const conversationLimitActions = {
  viewReceived: (view) => ({ type: VIEW_RECEIVED, payload: view }),
  seatAdded: (clientId) => ({ type: SEAT_ADDED, payload: clientId }),
  seatRemoved: (clientId) => ({ type: SEAT_REMOVED, payload: clientId }),
  /** The agent is ending or releasing this conversation from this tab. */
  ownUnseatStarted: (clientId) => ({ type: OWN_UNSEAT_STARTED, payload: clientId }),
  /** That request failed: the seat stays, so a later unseat is somebody else's. */
  ownUnseatSettled: (clientId) => ({ type: OWN_UNSEAT_SETTLED, payload: clientId }),
  capacityReceived: (capacity) => ({ type: CAPACITY_RECEIVED, payload: capacity }),
  /** `{waiting}` plus `receivedAt` (ISO): when the queue starts filling, that is the oldest wait. */
  queueChanged: (queue) => ({ type: QUEUE_CHANGED, payload: queue }),
  /** One agent's load from the company channel; `self` = it is the logged-in user's own. */
  agentLoadReceived: (load, self) => ({ type: AGENT_LOAD_RECEIVED, payload: load, self: self === true }),
  /** `{ quiet: true }`: the panel re-reads in the background (no spinner, no error shown). */
  loadRequest: (options) => ({ type: LOAD_REQUEST, payload: options }),
  loadSuccess: (load) => ({ type: LOAD_SUCCESS, payload: load }),
  loadFailure: (message) => ({ type: LOAD_FAILURE, payload: message }),
  /** `{ discardStale: true }` when the panel (re)opens: the form must not seed from an old config. */
  configRequest: (options) => ({ type: CONFIG_REQUEST, payload: options }),
  configSaveRequest: () => ({ type: CONFIG_SAVE_REQUEST }),
  configSuccess: (config) => ({ type: CONFIG_SUCCESS, payload: config }),
  configFailure: (message) => ({ type: CONFIG_FAILURE, payload: message }),
};

const isId = (value) => typeof value === 'string' && value !== '';
const isNonNegativeInt = (value) => Number.isInteger(value) && value >= 0;

/** Marks only live while their seat does; same array when nothing is dropped. */
const ownedBy = (ownUnseatIds, seatIds) =>
  ownUnseatIds.every((id) => seatIds.includes(id)) ? ownUnseatIds : ownUnseatIds.filter((id) => seatIds.includes(id));

const fromView = (state, view) => {
  if (!view || view.enabled !== true) return CONVERSATION_LIMIT_INITIAL_STATE;
  const { limit, activeCount, availableSlots, status, seats, queueWaiting } = view;
  const seatIds = seats.map((seat) => seat.clientId);
  return {
    ...state,
    enabled: true,
    capacity: { limit, activeCount, availableSlots, status },
    seatIds,
    ownUnseatIds: ownedBy(state.ownUnseatIds, seatIds),
    queueWaiting,
  };
};

const withCapacity = (state, payload) => {
  if (!payload || !Number.isInteger(payload.limit) || payload.limit < 1) return state;
  if (!isNonNegativeInt(payload.activeCount)) return state;
  const { limit, activeCount } = payload;
  const status = CAPACITY_STATUS_VALUES.includes(payload.status) ? payload.status : deriveStatus(activeCount, limit);
  return { ...state, capacity: { limit, activeCount, availableSlots: Math.max(0, limit - activeCount), status } };
};

/**
 * A seat event changed the seat set: the indicator follows it right away
 * instead of waiting for the next `conversationLimitCapacity` or `/me`. The
 * count is the SIZE of the set (not a +1/-1), so a capacity event that arrives
 * before its seat event is never counted twice. Without a known limit there is
 * nothing to derive and only the set changes.
 */
const withSeats = (state, seatIds) => {
  const { limit } = state.capacity;
  const ownUnseatIds = ownedBy(state.ownUnseatIds, seatIds);
  if (!Number.isInteger(limit) || limit < 1) return { ...state, seatIds, ownUnseatIds };
  const activeCount = seatIds.length;
  const capacity = {
    limit,
    activeCount,
    availableSlots: Math.max(0, limit - activeCount),
    status: deriveStatus(activeCount, limit),
  };
  return { ...state, seatIds, ownUnseatIds, capacity };
};

const isAgentLoad = (payload) =>
  !!payload && isId(payload.agentId) && Number.isInteger(payload.limit) && payload.limit >= 1
  && isNonNegativeInt(payload.activeCount);

/** A row with the event's numbers; name and last connection stay. */
const withAgentLoad = (row, load) => ({
  ...row,
  limit: load.limit,
  activeCount: load.activeCount,
  status: CAPACITY_STATUS_VALUES.includes(load.status) ? load.status : deriveStatus(load.activeCount, load.limit),
});

const withAgentLoads = (agents, loads) =>
  agents.map((row) => (loads[row.id] ? withAgentLoad(row, loads[row.id]) : row));

/**
 * Empty: no oldest. Starting to fill: the oldest arrived now. Growing: the
 * oldest is the same. Shrinking without emptying: the oldest may have left;
 * the panel re-reads quietly (`useAgentLoadRefresh`) and keeps it meanwhile.
 */
const nextQueue = (queue, waiting, receivedAt) => {
  if (waiting === 0) return { waiting: 0, oldestEnqueuedAt: null };
  const now = typeof receivedAt === 'string' && receivedAt ? receivedAt : null;
  const oldestEnqueuedAt = queue.waiting === 0 ? now : queue.oldestEnqueuedAt;
  return { waiting, oldestEnqueuedAt };
};

const withLoadData = (state, data) => ({ ...state, load: { ...state.load, data } });

/**
 * `conversationLimitAgentLoad`: the "Carga por agente" row and, when it is
 * the admin's own, their "N de L" (admins only listen to `company_<id>`). An
 * event read before the last one applied to that agent (`at`) arrived late
 * and is dropped.
 */
const reduceAgentLoad = (state, payload, self) => {
  if (!isAgentLoad(payload)) return state;
  const at = isNonNegativeInt(payload.at) ? payload.at : null;
  const last = state.live.at[payload.agentId];
  if (at !== null && Number.isInteger(last) && at < last) return state;
  const live = {
    ...state.live,
    at: at === null ? state.live.at : { ...state.live.at, [payload.agentId]: at },
    sinceLoad: { ...state.live.sinceLoad, [payload.agentId]: payload },
  };
  const next = self ? withCapacity({ ...state, live }, payload) : { ...state, live };
  const { data } = state.load;
  if (!data || !data.agents.some((row) => row.id === payload.agentId)) return next;
  return withLoadData(next, { ...data, agents: withAgentLoads(data.agents, { [payload.agentId]: payload }) });
};

const reduceQueueChanged = (state, payload) => {
  if (!payload || !isNonNegativeInt(payload.waiting)) return state;
  const live = { ...state.live, waitingSinceLoad: payload.waiting, waitingSinceLoadAt: payload.receivedAt || null };
  const next = { ...state, queueWaiting: payload.waiting, live };
  const { data } = state.load;
  if (!data || data.queue.waiting === payload.waiting) return next;
  return withLoadData(next, { ...data, queue: nextQueue(data.queue, payload.waiting, payload.receivedAt) });
};

/** A /load response with what arrived live while it was in flight applied on top. */
const loadWithLive = (state, load) => {
  const { sinceLoad, waitingSinceLoad, waitingSinceLoadAt } = state.live;
  const waiting = isNonNegativeInt(waitingSinceLoad) ? waitingSinceLoad : load.queue.waiting;
  return {
    ...load,
    queue: waiting === load.queue.waiting ? load.queue : nextQueue(load.queue, waiting, waitingSinceLoadAt),
    agents: withAgentLoads(load.agents, sinceLoad),
  };
};

const reduceOwnUnseat = (state, { type, payload }) => {
  const marked = state.ownUnseatIds.includes(payload);
  if (type === OWN_UNSEAT_STARTED) {
    return state.seatIds.includes(payload) && !marked ? { ...state, ownUnseatIds: [...state.ownUnseatIds, payload] } : state;
  }
  return marked ? { ...state, ownUnseatIds: state.ownUnseatIds.filter((id) => id !== payload) } : state;
};

const reduceEnabled = (state, { type, payload }) => {
  switch (type) {
    case SEAT_ADDED:
      return isId(payload) && !state.seatIds.includes(payload) ? withSeats(state, [...state.seatIds, payload]) : state;
    case SEAT_REMOVED:
      return state.seatIds.includes(payload) ? withSeats(state, state.seatIds.filter((id) => id !== payload)) : state;
    case OWN_UNSEAT_STARTED:
    case OWN_UNSEAT_SETTLED:
      return reduceOwnUnseat(state, { type, payload });
    case CAPACITY_RECEIVED:
      return withCapacity(state, payload);
    case QUEUE_CHANGED:
      return reduceQueueChanged(state, payload);
    default:
      return state;
  }
};

const reducePanels = (state, { type, payload, self }) => {
  switch (type) {
    case AGENT_LOAD_RECEIVED:
      return reduceAgentLoad(state, payload, self);
    case LOAD_REQUEST: {
      const live = { ...state.live, sinceLoad: {}, waitingSinceLoad: null, waitingSinceLoadAt: null };
      if (payload && payload.quiet === true) return { ...state, live };
      // Opening the panel (or a manual reload) starts a fresh ordering, like the app's panel.
      return { ...state, live: { ...live, at: {} }, load: { ...state.load, fetching: true, error: null } };
    }
    case LOAD_SUCCESS: {
      // A load answered with `enabled: false` carries an empty queue, not a real count.
      if (payload.enabled !== true) return { ...state, load: { fetching: false, data: payload, error: null } };
      const data = loadWithLive(state, payload);
      return { ...state, queueWaiting: data.queue.waiting, load: { fetching: false, data, error: null } };
    }
    case LOAD_FAILURE:
      return { ...state, load: { fetching: false, data: null, error: payload } };
    case CONFIG_REQUEST: {
      // Opening the panel drops the previous config so nobody edits (and saves over) stale values.
      const data = payload && payload.discardStale === true ? null : state.config.data;
      return { ...state, config: { ...state.config, data, fetching: true, error: null } };
    }
    case CONFIG_SAVE_REQUEST:
      return { ...state, config: { ...state.config, saving: true, error: null } };
    case CONFIG_SUCCESS:
      return { ...state, config: { fetching: false, saving: false, data: payload, error: null } };
    case CONFIG_FAILURE:
      return { ...state, config: { ...state.config, fetching: false, saving: false, error: payload } };
    default:
      return state;
  }
};

export default function conversationLimitReducer(state = CONVERSATION_LIMIT_INITIAL_STATE, action) {
  if (action.type === VIEW_RECEIVED) return fromView(state, action.payload);
  if (action.type === CLEAR_DATA) return CONVERSATION_LIMIT_INITIAL_STATE;
  if (!state.enabled) return state;
  const afterPanels = reducePanels(state, action);
  return afterPanels !== state ? afterPanels : reduceEnabled(state, action);
}
